import React from 'react';
import classNames from 'classnames';
import { useId } from '@reach/auto-id';
import { CheckIcon } from '@phosphor-icons/react';
import { RCCheckableLabel, labelID, sublabelID, errorID } from '../RCCheckbox/RCCheckableLabel';
import { CustomTagProps, CommonInputProps } from '../../types';

export interface RCCheckboxCardProps extends Omit<CommonInputProps, 'helpText' | 'variant' | 'tag'> {
  /** Passed through to the input tag. */
  checked?: boolean;

  /** Allows for the label text to be visually hidden, while still providing the proper labeling and context to screen readers. */
  visuallyHiddenLabel?: boolean;

  sublabel?: React.ReactNode;

  customTag?: CustomTagProps;
}

/**
 * A checkbox styled as a large clickable card, similar to RCRadioCardGroup.Option.
 */
export function RCCheckboxCard({
  checked = false,
  disabled = false,
  errorText = undefined,
  id: propId = undefined,
  name = undefined,
  onChange = undefined,
  label,
  sublabel = undefined,
  required = false,
  visuallyHiddenLabel = false,
  customTag = undefined,
}: RCCheckboxCardProps) {
  const autoId = useId();
  const id = propId || `rc-checkbox-card-${autoId}`;

  const describedBy = (sublabel || errorText) ? [
    sublabel && sublabelID(id),
    errorText && errorID(id),
  ].filter(Boolean).join(' ') : undefined;

  return (
    <div
      className={classNames(
        'rc-checkbox-card',
        { 'rc-checkbox-card--disabled': disabled },
        { 'rc-checkbox-card--checked': checked },
      )}
    >
      <div className="rc-checkbox-card__main">
        <span className={classNames(
          'rc-checkbox',
          { 'rc-checkbox--checked': checked },
          { 'rc-checkbox--disabled': disabled },
        )}>
          <input
            className="rc-checkbox__input"
            type="checkbox"
            name={name}
            id={id}
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            required={required}
            aria-labelledby={labelID(id)}
            aria-describedby={describedBy}
          />
          <span className="rc-checkbox__box">
            <span className="rc-checkbox__icon">
              <CheckIcon weight="bold" />
            </span>
          </span>
          <RCCheckableLabel
            htmlFor={id}
            sublabel={sublabel}
            label={label}
            errorText={errorText}
            inputId={id}
            visuallyHiddenLabel={visuallyHiddenLabel}
            customTag={customTag}
          />
        </span>
      </div>
    </div>
  );
}
